import React, { useEffect, useMemo, useState } from 'react';
import { List } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const slugify = (s, i) =>
  (s || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60) || `section-${i}`;

/**
 * Parse an HTML string and return:
 *   - items: [{ id, text }] — one per H2
 *   - html:  the same HTML with id attributes injected onto each <h2>
 *
 * This lets the parent render the modified HTML directly via
 * dangerouslySetInnerHTML so the anchors survive React re-renders.
 */
export const parseHeadings = (contentHtml) => {
  if (!contentHtml) return { items: [], html: '' };
  if (typeof window === 'undefined') return { items: [], html: contentHtml };

  const parser = new DOMParser();
  const doc = parser.parseFromString(contentHtml, 'text/html');
  // Accept H1/H2/H3 so the TOC works regardless of which heading level
  // the author chose in the rich-text editor (Quill exposes 1/2/3 as
  // options and authors don't always pick H2).
  const headers = Array.from(doc.body.querySelectorAll('h1, h2, h3'));
  const items = headers.map((h, i) => {
    const text = (h.textContent || '').trim();
    const id = `toc-${i}-${slugify(text, i)}`;
    h.id = id;
    h.setAttribute('style', `${h.getAttribute('style') || ''} scroll-margin-top:90px;`);
    // Track the level so the TOC list can indent nested headers.
    const level = parseInt(h.tagName.replace('H', ''), 10) || 2;
    return { id, text, level };
  });
  return { items, html: doc.body.innerHTML };
};

/**
 * Renders a table of contents. Must be paired with a parent that renders the
 * modified HTML from parseHeadings(contentHtml).html so the id anchors exist.
 */
export const BlogTableOfContents = ({ items, articleSelector = '[data-testid="blog-post-content"]' }) => {
  const { t } = useLanguage();
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (!items.length) return;
    const root = document.querySelector(articleSelector);
    if (!root) return;
    const headers = Array.from(root.querySelectorAll('h1, h2, h3'));
    if (!headers.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -65% 0px', threshold: 0 }
    );
    headers.forEach(h => observer.observe(h));
    return () => observer.disconnect();
  }, [items, articleSelector]);

  if (items.length < 2) return null;

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const absoluteTop = el.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: absoluteTop - 80, behavior: 'smooth' });
      setActiveId(id);
    }
  };

  return (
    <nav
      className="mb-8 p-4 rounded-xl border bg-muted/40"
      aria-label="Table of contents"
      data-testid="blog-toc"
    >
      <div className="flex items-center gap-2 mb-3 text-sm font-semibold">
        <List className="h-4 w-4 text-[#00E5CC]" />
        {t('tableOfContents')}
      </div>
      <ol className="space-y-1.5 pl-1">
        {items.map((item, i) => (
          <li key={item.id} className="flex gap-2 items-baseline text-sm min-w-0">
            <span className="text-muted-foreground tabular-nums w-6 shrink-0">
              {String(i + 1).padStart(2, '0')}
            </span>
            <a
              href={`#${item.id}`}
              onClick={(e) => handleClick(e, item.id)}
              className={`hover:text-[#00E5CC] transition-colors leading-snug break-words min-w-0 flex-1 ${
                activeId === item.id
                  ? 'text-[#00E5CC] font-medium'
                  : 'text-foreground/85'
              } ${item.level === 3 ? 'pl-4' : ''}`}
              data-testid={`toc-link-${i}`}
            >
              {item.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

/**
 * Hook: parse a content HTML string once and memoize the result.
 */
export const useTocFromHtml = (contentHtml) =>
  useMemo(() => parseHeadings(contentHtml), [contentHtml]);

export default BlogTableOfContents;
